import { Fragment, useEffect, useState } from 'react';
import { ArrowRight, Check, Minus } from 'lucide-react';
import { SaudiContactForm } from '@/components/pricing/SaudiContactForm';
import { Button } from '@/components/ui/button';
import { DemoRequestDialog } from '@/components/landing/DemoRequestDialog';
import { PricingStructuredData } from '@/components/seo/PricingStructuredData';
import { AnimatedNumber } from '@/components/home/primitives';
import { ScreeningCostCalculator } from '@/components/home/ConversionSections';
import { BillingToggle, CtaPanel, FaqSection, PageHero, PageShell, Reveal, Section, SectionHeading, SmartLink } from '@/components/page/PageKit';
import { MARKETING_PLANS, PRICING_FAQS } from '@/data/marketingContent';
import { useSEO } from '@/hooks/useSEO';
import { cn } from '@/lib/utils';

const groups = ['Jobs & candidates', 'Interviews & assessments', 'Collaboration', 'Branding', 'Integrations'];

export default function Pricing() {
  const [isSaudi, setIsSaudi] = useState<boolean | null>(null);
  const [annual, setAnnual] = useState(true);
  const [demo, setDemo] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch('https://ipapi.co/json/', { signal: controller.signal }).then(r => (r.ok ? r.json() : Promise.reject())).then(d => setIsSaudi(d.country_code === 'SA')).catch(() => setIsSaudi(false));
    return () => controller.abort();
  }, []);
  useSEO({ title: 'Recruiting Agency Software Pricing | Beatview', description: 'Beatview pricing for recruitment agencies: Free with one active job, Pro at $179 a month billed annually with AI video interviews, and custom Enterprise plans.', canonicalUrl: 'https://beatview.ai/pricing' });

  if (isSaudi) {
    return <PageShell><PricingStructuredData /><SaudiContactForm /><DemoRequestDialog open={demo} onOpenChange={setDemo} /></PageShell>;
  }

  return (
    <PageShell>
      <PricingStructuredData />
      <PageHero align="center" eyebrow="Clear plan boundaries" title={<>Start free. Add AI interviews <span className="bv-hero-highlight">when your agency is ready.</span></>} description="Compare active jobs, interviews, collaboration, branding and integrations before you choose.">
        <div className="flex justify-center"><BillingToggle annual={annual} onChange={setAnnual} /></div>
      </PageHero>

      <section className="relative bg-bv-base pb-20 lg:pb-28" aria-label="Plans">
        <div className="mx-auto grid max-w-[1240px] gap-5 px-5 lg:grid-cols-3 lg:px-8">
          {MARKETING_PLANS.map((plan, i) => {
            const price = annual ? plan.annualMonthly : plan.monthly;
            const featured = !!plan.featured;
            return (
              <Reveal key={plan.name} delay={i * 0.08} className={cn(featured && 'order-first lg:order-none')}>
                <article className={cn('relative flex h-full flex-col overflow-hidden rounded-3xl p-7 sm:p-8', featured ? 'bg-bv-ink text-white shadow-[0_40px_90px_-40px_hsl(var(--navy)/0.7)] ring-1 ring-white/10' : 'border border-bv-blue-line bg-white')}>
                  {featured && <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[hsl(var(--hero-highlight-start)/0.35)] blur-[80px]" />}
                  <div className="relative min-h-[112px]">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className={cn('font-display text-2xl font-semibold', featured ? 'text-white' : 'text-bv-ink')}>{plan.name}</h2>
                      {featured && <span className="bv-btn-gradient rounded-full px-3 py-1 text-[12px] font-semibold text-white">Recommended</span>}
                    </div>
                    {plan.eyebrow && <p className={cn('mt-1 text-xs font-semibold uppercase tracking-wider', featured ? 'text-bv-blue-bright' : 'text-bv-coral')}>{plan.eyebrow}</p>}
                    <p className={cn('mt-3 text-[15px] leading-6', featured ? 'text-white/70' : 'text-bv-ink2')}>{plan.description}</p>
                  </div>
                  <div className="relative mt-6 min-h-[96px]">
                    {price === null
                      ? <p className="font-display text-4xl font-semibold">Custom</p>
                      : <p className="flex items-baseline gap-1"><span className="font-display text-6xl font-semibold tracking-[-0.04em] tabular-nums"><AnimatedNumber value={price} format={v => `$${Math.round(v)}`} /></span><span className={cn('text-[15px]', featured ? 'text-white/60' : 'text-bv-ink2')}>/ month</span></p>}
                    {annual && plan.annualTotal && <p className={cn('mt-1 text-[13px]', featured ? 'text-white/60' : 'text-bv-mute')}>Billed annually at ${plan.annualTotal.toLocaleString('en-US')}</p>}
                    {price === 0 && <p className="mt-1 text-[13px] text-bv-mute">No credit card required</p>}
                  </div>
                  <Button asChild variant={featured ? 'default' : 'outline'} className={cn('relative mt-6 h-12 w-full rounded-xl text-[15px] font-semibold', featured ? 'bv-shine' : 'border-bv-blue-line bg-white text-bv-ink hover:bg-bv-blue-wash hover:text-bv-ink')}>
                    <SmartLink href={plan.href}>{plan.cta} <ArrowRight className="h-4 w-4" /></SmartLink>
                  </Button>
                  <ul className={cn('relative mt-7 flex-1 space-y-3 border-t pt-7', featured ? 'border-white/10' : 'border-bv-blue-line')}>
                    {plan.features.map(feature => (
                      <li key={feature.label} className={cn('flex gap-2.5 text-[15px] leading-6', !feature.included && 'opacity-60', featured ? 'text-white/80' : 'text-bv-ink2')}>
                        <span className={cn('mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full', feature.included ? (featured ? 'bg-bv-success text-white' : 'bg-bv-success-soft text-bv-success') : (featured ? 'bg-white/10 text-white/60' : 'bg-bv-blue-wash text-bv-mute'))}>
                          {feature.included ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <Minus className="h-2.5 w-2.5" strokeWidth={3} />}
                        </span>
                        <span><strong className={featured ? 'text-white' : 'text-bv-ink'}>{feature.label}:</strong> {feature.value}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Section tone="wash" labelledBy="plan-compare-title">
        <SectionHeading id="plan-compare-title" eyebrow="Side by side" title="Compare plan details" />
        <Reveal delay={0.1} className="mt-10">
          <div className="overflow-x-auto rounded-3xl border border-bv-blue-line bg-white shadow-bv-card" role="region" aria-label="Beatview plan feature comparison" tabIndex={0}>
            <table className="w-full min-w-[820px] border-collapse text-left text-[15px]">
              <caption className="sr-only">Beatview plan feature comparison</caption>
              <thead>
                <tr className="bg-bv-blue-wash/60">
                  <th scope="col" className="px-5 py-4 text-sm font-semibold text-bv-ink">Feature</th>
                  {MARKETING_PLANS.map(plan => <th key={plan.name} scope="col" className={cn('relative px-5 py-4 text-sm font-semibold', plan.featured ? 'bg-bv-ink text-white' : 'text-bv-ink')}>{plan.featured && <span aria-hidden className="bv-btn-gradient absolute inset-x-0 top-0 h-[3px]" />}{plan.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {groups.map(group => (
                  <Fragment key={group}>
                    <tr className="border-t border-bv-blue-line bg-bv-blue-wash/30"><th colSpan={4} scope="colgroup" className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-bv-mute">{group}</th></tr>
                    {Array.from(new Set(MARKETING_PLANS.flatMap(plan => plan.features.filter(f => f.group === group).map(f => f.label)))).map(label => (
                      <tr key={`${group}-${label}`} className="border-t border-bv-blue-line">
                        <th scope="row" className="px-5 py-4 font-semibold text-bv-ink">{label}</th>
                        {MARKETING_PLANS.map(plan => {
                          const feature = plan.features.find(f => f.label === label);
                          return <td key={plan.name} className={cn('px-5 py-4', plan.featured ? 'bg-bv-blue-wash/40 font-medium text-bv-ink' : 'text-bv-ink2')}><span className="inline-flex items-center gap-2">{feature?.included ? <Check className="h-4 w-4 text-bv-success" /> : <Minus className="h-4 w-4 text-bv-mute" />}{feature?.value || 'Not included'}</span></td>;
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </Section>

      <ScreeningCostCalculator />
      <FaqSection title="Pricing questions" items={PRICING_FAQS} idPrefix="pricing-faq" tone="base" />
      <CtaPanel title={<>Start with one role. <span className="bv-hero-highlight">Add more when it pays for itself.</span></>} description="The Free plan includes one active job with unlimited candidates. Upgrade to Pro when you want AI video interviews." onDemo={() => setDemo(true)} />
      <DemoRequestDialog open={demo} onOpenChange={setDemo} />
    </PageShell>
  );
}
